/**
 * Custom Domain card (INT-0007) — Settings → General.
 *
 * Lifecycle UI over the dedicated customDomain endpoints: enter a domain →
 * CNAME instructions → status polls pending_dns → pending_ssl → active →
 * Remove. Free for every org. Polls every 5s ONLY while a non-terminal
 * status is on screen; on localhost (no Cloudflare) the card renders a
 * production-only note. Status fetches are fail-safe null (the card must
 * never take down Settings — stripeStatus precedent).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/auth/clerkShim';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Globe, ExternalLink, Trash2, Loader2 } from 'lucide-react';
import useOrganizationStore from '@store/organizationStore';
import { isLocalAdminHost } from '@/utils/publicSiteUrl';

const POLL_MS = 5000;

const STATUS_COPY: Record<string, { label: string; hint: string; badge: 'default' | 'secondary' | 'destructive' }> = {
  pending_dns: {
    label: 'Waiting for DNS',
    hint: 'Add the CNAME record below at your domain provider. DNS changes usually take a few minutes.',
    badge: 'secondary',
  },
  pending_ssl: {
    label: 'Issuing certificate',
    hint: 'DNS found. The security certificate is being issued. Almost there.',
    badge: 'secondary',
  },
  active: {
    label: 'Live',
    hint: 'Your site is served on this domain. The Staty subdomain now redirects here.',
    badge: 'default',
  },
  failed: {
    label: 'Problem',
    hint: 'Something blocked setup. Check the detail below, fix the record, and setup resumes automatically, or remove the domain and start over.',
    badge: 'destructive',
  },
};

interface DomainState {
  hostname: string | null;
  status?: string;
  cnameTarget?: string;
  isApex?: boolean;
  configured?: boolean;
  detail?: { hostnameStatus?: string; sslStatus?: string; error?: string };
  error?: string;
}

export default function CustomDomainCard({ organizationId }: { organizationId?: string }) {
  const { getToken } = useAuth();
  const {
    setCustomDomain: setFn,
    getCustomDomainStatus: statusFn,
    removeCustomDomain: removeFn,
  } = useOrganizationStore((s: any) => ({
    setCustomDomain: s.setCustomDomain,
    getCustomDomainStatus: s.getCustomDomainStatus,
    removeCustomDomain: s.removeCustomDomain,
  }));
  const setCustomDomain = useMemo(() => setFn(getToken), [setFn, getToken]);
  const getCustomDomainStatus = useMemo(() => statusFn(getToken), [statusFn, getToken]);
  const removeCustomDomain = useMemo(() => removeFn(getToken), [removeFn, getToken]);

  const [domain, setDomain] = useState<DomainState | null>(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    if (!organizationId) return;
    const result = await getCustomDomainStatus(organizationId);
    if (result) setDomain(result);
    setLoading(false);
  }, [organizationId, getCustomDomainStatus]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll only while a non-terminal status is on screen.
  const isPending = domain?.hostname && (domain.status === 'pending_dns' || domain.status === 'pending_ssl');
  useEffect(() => {
    if (!isPending) return undefined;
    pollRef.current = setInterval(refresh, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isPending, refresh]);

  const handleConnect = async () => {
    if (!organizationId || !input.trim()) return;
    setBusy(true);
    try {
      const result = await setCustomDomain(organizationId, input.trim().toLowerCase());
      setDomain(result);
      setInput('');
      toast.success('Domain added. Now create the CNAME record below.');
    } catch (err: any) {
      toast.error(err?.message || 'Could not set up the domain.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!organizationId) return;
    setBusy(true);
    try {
      await removeCustomDomain(organizationId);
      setDomain({ hostname: null, configured: domain?.configured });
      toast.success('Domain removed. Your Staty subdomain serves the site again.');
    } catch (err: any) {
      toast.error(err?.message || 'Could not remove the domain.');
    } finally {
      setBusy(false);
    }
  };

  const copy = domain?.status ? STATUS_COPY[domain.status] : null;
  const localOnly = isLocalAdminHost();

  return (
    <Card className="shadow-sm border-0" data-testid="custom-domain-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Custom domain
        </CardTitle>
        <CardDescription>
          Serve your league site on your own domain, included free. Your Staty
          address keeps working and redirects once the domain is live.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {localOnly ? (
          <p className="text-sm text-muted-foreground">
            Custom domains are set up on production (dash.staty.io). This is a
            local environment.
          </p>
        ) : loading ? (
          <p className="text-sm text-muted-foreground">Checking domain status…</p>
        ) : !domain?.hostname ? (
          <div className="space-y-3">
            <div>
              <Label htmlFor="custom-domain-input">Domain</Label>
              <Input
                id="custom-domain-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="leagues.example.com"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground mt-1">
                A subdomain like <span className="font-mono">leagues.example.com</span> is
                the most reliable choice: bare domains (example.com) work only when
                your DNS provider supports CNAME flattening.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={busy || !input.trim()}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Connect domain
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium font-mono truncate">{domain.hostname}</p>
                {copy && (
                  <p className="text-xs text-muted-foreground mt-0.5">{copy.hint}</p>
                )}
              </div>
              {copy && (
                <Badge variant={copy.badge} data-testid="custom-domain-status">
                  {isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                  {copy.label}
                </Badge>
              )}
            </div>

            {domain.status !== 'active' && domain.cnameTarget && (
              <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="cname-instructions">
                <p className="font-medium mb-1">Create this DNS record at your domain provider:</p>
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono text-xs">
                  <span className="text-muted-foreground">Type</span><span>CNAME</span>
                  <span className="text-muted-foreground">Name</span><span>{domain.hostname}</span>
                  <span className="text-muted-foreground">Target</span><span>{domain.cnameTarget}</span>
                </div>
                {domain.isApex && (
                  <p className="text-xs text-muted-foreground mt-2">
                    This is a bare domain. Your DNS provider must support CNAME
                    flattening (also called ANAME or ALIAS records).
                  </p>
                )}
              </div>
            )}

            {domain.detail?.error && domain.status !== 'active' && (
              <p className="text-xs text-destructive">{domain.detail.error}</p>
            )}
            {domain.error === 'unavailable' && (
              <p className="text-xs text-muted-foreground">
                Status check is temporarily unavailable. Showing the last known state.
              </p>
            )}

            <div className="flex items-center gap-2">
              {domain.status === 'active' && (
                <Button variant="outline" size="sm" asChild>
                  <a href={`https://${domain.hostname}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Open site
                  </a>
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleRemove} disabled={busy}>
                <Trash2 className="h-4 w-4 mr-1" />
                Remove domain
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
